import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import GroupIcon from "@material-ui/icons/Group";
import ScheduleIcon from "@material-ui/icons/Schedule";
import { format } from "date-fns";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  panel: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1)
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(2)
  },
  infoRow: {
    alignItems: "center",
    display: "flex",
    marginBottom: theme.spacing(1.5)
  },
  infoIcon: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1)
  },
  infoLabel: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    marginRight: theme.spacing(1)
  },
  infoValue: {
    color: theme.palette.text.primary,
    fontWeight: 600
  },
  progressBar: {
    borderRadius: 5,
    height: 10
  }
}));

const statusLabel = (status) => ({
  scheduled: "Programada",
  processing: "En curso",
  completed: "Completada",
  failed: "Fallida",
  cancelled: "Cancelada"
}[status] || status);

const statusColor = (status) => ({
  scheduled: "#2196f3",
  processing: "#ff9800",
  completed: "#4caf50",
  failed: "#f44336",
  cancelled: "#757575"
}[status] || "#757575");

const formatDuration = (minutes) => {
  if (minutes < 60) return `${Math.round(minutes)} min`;
  return `${Math.floor(minutes / 60)}h ${Math.round(minutes % 60)}min`;
};

const EstimationsPanel = ({ campaigns, selectedCampaignId, setSelectedCampaignId }) => {
  const classes = useStyles();
  const [estimations, setEstimations] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedCampaignId) {
      setEstimations(null);
      return undefined;
    }

    let mounted = true;
    const loadEstimations = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/group-campaign-shipping-reports/estimations/${selectedCampaignId}`
        );
        if (mounted) setEstimations(data);
      } catch (err) {
        if (mounted) toastError(err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadEstimations();
    const intervalId = setInterval(loadEstimations, 10000);
    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, [selectedCampaignId]);

  const progress = estimations?.currentProgress;
  const progressPct =
    progress && progress.total > 0
      ? Math.min(100, (progress.sent / progress.total) * 100)
      : 0;

  return (
    <Paper className={classes.panel}>
      <Typography variant="h6" className={classes.title}>
        Estimaciones de Envio
      </Typography>
      <Box mb={3}>
        <TextField
          select
          label="Seleccionar Campaña"
          value={selectedCampaignId}
          onChange={(event) => setSelectedCampaignId(event.target.value)}
          fullWidth
          variant="outlined"
        >
          <MenuItem value="">Seleccione una campaña</MenuItem>
          {campaigns.map((campaign) => (
            <MenuItem key={campaign.id} value={campaign.id}>
              {campaign.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      {loading && <LinearProgress />}
      {estimations && !loading && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box className={classes.infoRow}>
              <GroupIcon className={classes.infoIcon} />
              <Typography className={classes.infoLabel}>Total grupos:</Typography>
              <Typography className={classes.infoValue}>{estimations.totalGroups}</Typography>
            </Box>
            <Box className={classes.infoRow}>
              <AccessTimeIcon className={classes.infoIcon} />
              <Typography className={classes.infoLabel}>Intervalo:</Typography>
              <Typography className={classes.infoValue}>{estimations.messageInterval} seg</Typography>
            </Box>
            <Box className={classes.infoRow}>
              <ScheduleIcon className={classes.infoIcon} />
              <Typography className={classes.infoLabel}>Intervalo mayor:</Typography>
              <Typography className={classes.infoValue}>
                cada {estimations.longerIntervalAfter} grupos ({estimations.greaterInterval} seg)
              </Typography>
            </Box>
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Estado:</Typography>
              <Chip
                size="small"
                label={statusLabel(estimations.status)}
                style={{ backgroundColor: statusColor(estimations.status), color: "white" }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Duracion estimada:</Typography>
              <Typography className={classes.infoValue}>
                {formatDuration(estimations.estimatedDuration)}
              </Typography>
            </Box>
            {estimations.estimatedStart && (
              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>Proximo inicio:</Typography>
                <Typography className={classes.infoValue}>
                  {format(new Date(estimations.estimatedStart), "dd/MM/yyyy HH:mm")}
                </Typography>
              </Box>
            )}
            {estimations.estimatedEnd && (
              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>Fin estimado:</Typography>
                <Typography className={classes.infoValue}>
                  {format(new Date(estimations.estimatedEnd), "dd/MM/yyyy HH:mm")}
                </Typography>
              </Box>
            )}
            {progress && (
              <Box mt={2}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography className={classes.infoLabel}>
                    Progreso: {progress.sent} / {progress.total}
                  </Typography>
                  <Typography className={classes.infoValue}>{Math.round(progressPct)}%</Typography>
                </Box>
                <LinearProgress
                  className={classes.progressBar}
                  variant="determinate"
                  value={progressPct}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      )}
      {!selectedCampaignId && !loading && (
        <Typography variant="body2" color="textSecondary" align="center">
          Seleccione una campaña para ver sus estimaciones.
        </Typography>
      )}
    </Paper>
  );
};

export default EstimationsPanel;
